import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, ColorPicker, Space, Typography, message } from 'antd';
import { CalendarOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import MainLayout from '../../layout/layout';
import { apiClient } from '../../../config/apiClient';

const { Text } = Typography;

const COLOR_PRESETS = [
  '#1890ff',
  '#52c41a',
  '#faad14',
  '#f5222d',
  '#722ed1',
  '#eb2f96',
  '#13c2c2',
  '#fa8c16',
  '#d8ff3e',
  '#2f54eb',
];

const toHex = (color) => {
  if (!color) return '#1890ff';
  if (typeof color === 'string') return color;
  if (typeof color.toHexString === 'function') return color.toHexString();
  return '#1890ff';
};

const DiasDisponibles = () => {
  const [dias, setDias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDias = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/torneos/config/dias-disponibles');
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || 'No se pudieron cargar los días disponibles');
        return;
      }
      const data = await response.json();
      setDias(Array.isArray(data?.dias) ? data.dias : []);
    } catch (error) {
      console.error('Error al cargar días disponibles:', error);
      message.error('Error al cargar los días disponibles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDias();
  }, [loadDias]);

  const updateDia = (diaSemana, patch) => {
    setDias((prev) => prev.map((dia) => (
      dia.diaSemana === diaSemana ? { ...dia, ...patch } : dia
    )));
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const response = await apiClient.put('/torneos/config/dias-disponibles', {
        dias: dias.map((dia) => ({
          diaSemana: dia.diaSemana,
          disponible: Boolean(dia.disponible),
          color: toHex(dia.color),
        })),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || 'No se pudieron guardar los días disponibles');
        return;
      }
      const data = await response.json();
      setDias(Array.isArray(data?.dias) ? data.dias : dias);
      message.success('Días disponibles guardados correctamente');
    } catch (error) {
      console.error('Error al guardar días disponibles:', error);
      message.error('Error al guardar los días disponibles');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <Card
        title={(
          <Space>
            <CalendarOutlined />
            <span>Días disponibles</span>
          </Space>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadDias} loading={loading}>
              Actualizar
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleGuardar} loading={saving} disabled={loading || dias.length === 0}>
              Guardar
            </Button>
          </Space>
        )}
        loading={loading}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Marcá qué días de la semana van a estar disponibles para torneos y asignales un color para identificarlos."
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {dias.map((dia) => (
            <div
              key={dia.diaSemana}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '12px 16px',
                borderRadius: 10,
                background: '#2a2a2a',
                border: '1px solid #343434',
                borderLeft: `6px solid ${toHex(dia.color)}`,
                opacity: dia.disponible ? 1 : 0.55,
                flexWrap: 'wrap',
              }}
            >
              <Checkbox
                checked={Boolean(dia.disponible)}
                onChange={(event) => updateDia(dia.diaSemana, { disponible: event.target.checked })}
              >
                Disponible
              </Checkbox>
              <Text strong style={{ minWidth: 110, flex: 1 }}>
                {dia.nombre}
              </Text>
              <Space align="center">
                <Text type="secondary">Color</Text>
                <ColorPicker
                  value={toHex(dia.color)}
                  showText
                  format="hex"
                  defaultFormat="hex"
                  disabledAlpha
                  presets={[{ label: 'Colores recomendados', colors: COLOR_PRESETS }]}
                  onChange={(color) => updateDia(dia.diaSemana, { color: toHex(color) })}
                />
              </Space>
            </div>
          ))}
        </div>
      </Card>
    </MainLayout>
  );
};

export default DiasDisponibles;
